import { apiRequest } from '@/lib/api-request';

const STORAGE_KEY_PREFIX = 'yapzap-tour-';

interface TourState {
  seenVersion: string;
  completedAt: string;
  neverShowAgain: boolean;
}

/**
 * Server-side completed tours are loaded into this cache on init.
 * localStorage acts as a fast fallback; the server record is authoritative.
 */
let serverToursCache: Record<string, TourState> | null = null;

/**
 * Called once at app startup (after user loads) to seed the cache
 * from the user object's completedTours field.
 */
export function initTourCache(completedTours: Record<string, TourState> | null | undefined): void {
  serverToursCache = completedTours ?? {};

  // Sync server state into localStorage so the fast path works
  if (serverToursCache) {
    for (const [tourId, state] of Object.entries(serverToursCache)) {
      try {
        localStorage.setItem(`${STORAGE_KEY_PREFIX}${tourId}`, JSON.stringify(state));
      } catch {}
    }
  }
}

function getLocalState(tourId: string): TourState | null {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${tourId}`);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

export function shouldShowTour(tourId: string, currentVersion: string): boolean {
  // Check server cache first (authoritative)
  const serverState = serverToursCache?.[tourId];
  if (serverState) {
    if (serverState.neverShowAgain) return false;
    return serverState.seenVersion !== currentVersion;
  }

  // Fall back to localStorage
  const localState = getLocalState(tourId);
  if (localState) {
    if (localState.neverShowAgain) return false;
    return localState.seenVersion !== currentVersion;
  }

  return true;
}

export function markTourCompleted(
  tourId: string,
  version: string,
  neverShowAgain = false,
): void {
  const state: TourState = {
    seenVersion: version,
    completedAt: new Date().toISOString(),
    neverShowAgain,
  };

  // Update localStorage (fast cache)
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${tourId}`, JSON.stringify(state));
  } catch {}

  // Update in-memory cache
  if (serverToursCache) {
    serverToursCache[tourId] = state;
  }

  // Persist to server (fire-and-forget)
  apiRequest('/api/user/tours/complete', 'POST', {
    tourId,
    version,
    neverShowAgain,
  }).catch((err) => {
    console.warn('Failed to persist tour completion to server:', err);
  });
}

export function resetTour(tourId: string): void {
  localStorage.removeItem(`${STORAGE_KEY_PREFIX}${tourId}`);
  if (serverToursCache) {
    delete serverToursCache[tourId];
  }
}

export function resetAllTours(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STORAGE_KEY_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
  serverToursCache = {};
}
